import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ButtonComponent from '../../components/Button.component';
import TextareaComponent from '../../components/Input/Textarea.component';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { updateUserEmailTemplateThunk } from '../../redux/User/UserThunk';
import { useAppSelector } from '../../hooks/useAppSelector.hook';
import { getUserEmailTemplates } from '../../redux/User/UserSlice';
import { EventStatusCode } from '../../redux/Event/Status/EventStatus.type';
import useEmailTemplate from '../../hooks/useEmailTemplate.hook';

interface Props {
  statusCode: EventStatusCode;
}

const STATUS_CODE_TO_TITLE = {
  [EventStatusCode.COMMUNICATION_REJECT]: 'correspondence',
  [EventStatusCode.PRE_INTERVIEW_REJECT]: 'preliminaryInterview',
  [EventStatusCode.INTERVIEW_REJECT]: 'interview',
  [EventStatusCode.INTERVIEW_PLANNED]: 'interview',
  [EventStatusCode.PRE_INTERVIEW_PLANNED]: 'preliminaryInterview',
};

const StyledHeader = styled.div`
  font-size: 20px;
  font-weight: 600;
`;

const StyledValidationMessage = styled.div`
  color: ${({ theme }) => theme.colors.RED.R400};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
`;

const StyledTitleButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`;

const StyledVariablesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const EmailTemplateBlock = ({ statusCode }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const emailTemplates = useAppSelector(getUserEmailTemplates);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newTemplateText, setNewTemplateText] = useState('');
  const {
    templateVariables,
    isTemplateVariablesCorrect,
    isTemplateLengthCorrect,
  } = useEmailTemplate({ templateText: newTemplateText });

  const oldEmailTemplate = useMemo(() => {
    return emailTemplates?.find(template => template.code === statusCode);
  }, [emailTemplates, statusCode]);

  const title = useMemo(() => {
    return t(STATUS_CODE_TO_TITLE[statusCode]);
  }, [statusCode, t]);

  const validationMessage = useMemo(() => {
    if (!newTemplateText) {
      return t('fieldCannotBeEmpty');
    }

    if (!isTemplateVariablesCorrect) {
      return t('checkVariablesFormat');
    }

    if (!isTemplateLengthCorrect) {
      return t('templateLengthExceeded');
    }
  }, [isTemplateLengthCorrect, isTemplateVariablesCorrect, newTemplateText, t]);

  const fillByDefaultTemplate = () => {
    setNewTemplateText(t('interviewDefaultTemplate'));
  };

  const onCancel = () => {
    setNewTemplateText(oldEmailTemplate?.template || newTemplateText);
    setIsEditMode(false);
  };

  const onSave = async () => {
    await dispatch(
      updateUserEmailTemplateThunk({
        code: statusCode,
        template: newTemplateText,
      }),
    );

    setIsEditMode(false);
  };

  useEffect(() => {
    setNewTemplateText(oldEmailTemplate?.template || '');
  }, [oldEmailTemplate?.template]);

  const variableButtons = templateVariables
    .filter(variable => !variable.hidden)
    .map(item => (
      <ButtonComponent
        handleClick={() =>
          setNewTemplateText(`${newTemplateText}${item.value}`)
        }
        key={item.name}
      >
        {item.name}
      </ButtonComponent>
    ));

  return (
    <div>
      <StyledTitleContainer>
        <StyledHeader>{title}</StyledHeader>

        <StyledTitleButtons>
          {isEditMode ? (
            <>
              <ButtonComponent handleClick={fillByDefaultTemplate}>
                {t('useTextTemplate')}
              </ButtonComponent>

              <ButtonComponent handleClick={onCancel}>
                {t('Cancel')}
              </ButtonComponent>

              <ButtonComponent
                disabled={!!validationMessage}
                appearance="primary"
                handleClick={onSave}
              >
                {t('Save')}
              </ButtonComponent>
            </>
          ) : (
            <ButtonComponent
              appearance="primary"
              handleClick={() => setIsEditMode(true)}
            >
              {t('Edit')}
            </ButtonComponent>
          )}
        </StyledTitleButtons>
      </StyledTitleContainer>

      {isEditMode ? (
        <>
          <StyledVariablesContainer>
            <span>{t('variablesAdding')}</span>

            {variableButtons}
          </StyledVariablesContainer>

          <StyledValidationMessage>{validationMessage}</StyledValidationMessage>

          <TextareaComponent
            value={newTemplateText}
            onChange={setNewTemplateText}
            maxLength={1000}
          />
        </>
      ) : (
        <div>{newTemplateText}</div>
      )}
    </div>
  );
};

export default EmailTemplateBlock;
